import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, Clock, Eye, Gift, Sparkles, Users } from 'lucide-react';
import styles from './index.module.less';
import classNames from 'classnames';
import { fetchInsMonitorSearch, fetchInsRecentUserSub, fetchRecentSearchData } from '@/lib/service';
import { InsAccountModel, RecentSearchUserList, RecentSubStatusModel } from '@/types/home';
import { useUserProvider } from '@/context/UserProvider';
import UserDashboard from '../Dashboard/UserDashboard';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { imgUrl } from '@/shared/utils';
import { useTranslation } from 'next-i18next';
import { Button, message, Tooltip } from 'antd';
import { cn } from '@/lib/utils';
import useMediaNameSync from '@/lib/hooks/useMediaNameSync';
import { QuestionCircleFilled } from '@ant-design/icons';
import { useRouter } from 'next/router';
import { Turnstile } from '@marsidev/react-turnstile';

const LocalImage = dynamic(() => import('../Common/LocalImage'), { ssr: true });
const FAQSection = dynamic(() => import('./FAQSection'), { ssr: true });
const ExploreMoreToolsSection = dynamic(() => import('./ExploreMoreToolsSection'), { ssr: true });
const UserReviewsSection = dynamic(() => import('./UserReviewsSection'), { ssr: true });
const HowItWorksSection = dynamic(() => import('./HowItWorksSection'), { ssr: true });
const UpgradeModal = dynamic(() => import('./UpgradeModal'), { ssr: false });
const GenderDistribution = dynamic(() => import('./GenderDistribution'), { ssr: false });
const InstagramUserInfo = dynamic(() => import('./InstagramUserInfo'), { ssr: false });
const UserUpdates = dynamic(() => import('./UserUpdates/index'), { ssr: true });
const ContrastCheck = dynamic(() => import('./ContrastCheck'), { ssr: true });
const RealStore = dynamic(() => import('./RealStore'), { ssr: true });
const ProductionFunction = dynamic(() => import('./ProductionFunc'), { ssr: true });
const CommonSimpleModal = dynamic(() => import('../Common/CommonSimpleModal'), { ssr: false });
const RefueingPkg = dynamic(() => import('./RefueingPkg'), { ssr: true });

// const CommonSimpleModal = dynamic(() => import('../Common/CommonSimpleModal'), { ssr: false })
// const PlanModal = dynamic(() => import('./PlanModal'), { ssr: false })

const images = [
  'https://res-ins.pumpsoul.com/ins_image/1715226267021510004.jpg',
  'https://res-ins.pumpsoul.com/ins_image/1715231944765698112.jpg',
  'https://res-ins.pumpsoul.com/ins_image/1715232052557283516.jpg',
  'https://res-ins.pumpsoul.com/ins_image/1715232014132880270.jpg',
  'https://res-ins.pumpsoul.com/ins_image/1715271421881722942.jpg',
  'https://res-ins.pumpsoul.com/ins_image/1715232201493612730.jpg',
  'https://res-ins.pumpsoul.com/ins_image/1715233527880123759.jpg',
  'https://res-ins.pumpsoul.com/ins_image/1715234676612624393.jpg',
  'https://res-ins.pumpsoul.com/ins_image/1715270486218568131.jpg',
  'https://res-ins.pumpsoul.com/ins_image/1715232601673693741.jpg',
  'https://res-ins.pumpsoul.com/ins_image/1715232171016468256.jpg',
  'https://res-ins.pumpsoul.com/ins_image/1715231972925490962.jpg',
  'https://res-ins.pumpsoul.com/ins_image/1715268339164740710.jpg',
  'https://res-ins.pumpsoul.com/ins_image/1715234717494124470.jpg',
  'https://res-ins.pumpsoul.com/ins_image/1715271458106769031.jpg',
  'https://res-ins.pumpsoul.com/ins_image/1715263176240124166.jpg',
  'https://res-ins.pumpsoul.com/ins_image/1715233472060278219.jpg',
];

const getRandomImages = () => {
  const randomImages = [];
  for (let i = 0; i < 3; i++) {
    const randomIndex = Math.floor(Math.random() * images.length);
    randomImages.push(images[randomIndex]);
  }
  return randomImages;
};

const isNotExpired = (expired_time: string | number) => {
  return expired_time && new Date(expired_time) > new Date();
};

export default function RecentlyFollowedTool() {
  const inputSectionRef = useRef(null);
  const [username, setUsername] = useMediaNameSync('', {
    removeAfterRead: true,
  });
  const [showUserName, setShowUserName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [stage, setStage] = useState(0);
  const [activeTab, setActiveTab] = useState<'follows' | 'followers'>('follows');
  const [followingData, setFollowingData] = useState<RecentSearchUserList>(null);
  const [followerData, setFollowerData] = useState<RecentSearchUserList>(null);
  const [currentInsUser, setCurrentInsUser] = useState<InsAccountModel>(null);
  const { isLogin, onOpenLoginModal, onChangedLoginView } = useUserProvider();
  const [followingPageNo, setFollowingPageNo] = useState(1);
  const [followerPageNo, setFollowerPageNo] = useState(1);
  const [showSubscriptionLimit, setShowSubscriptionLimit] = useState(false);
  const { t } = useTranslation();
  const [openPrivateAccount, setOpenPrivateAccount] = useState<any>(null);
  const router = useRouter();
  const [hasNoData, setHasNoData] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const turnstileRef = useRef<any>(null);
  const nextCallRef = useRef<any>(null);
  const isLangLanguage = ['th', 'ja', 'es'].includes(router.locale);
  const [isFirstSearch, setIsFirstSearch] = useState(true);
  // 加油包
  const [openRefueingPkg, setOpenRefueingPkg] = useState(false);

  const needTurnstile = useMemo(() => {
    let currentTagNeed;
    if (activeTab === 'follows') {
      currentTagNeed = !followingData?.items?.length;
    } else {
      currentTagNeed = !followerData?.items?.length;
    }
    return !isLogin && currentTagNeed;
  }, [isLogin, activeTab, followingData, followerData]);

  const words = useMemo(() => {
    return [
      { text: t('Followed'), color: '#10B981' },
      { text: t('Follow'), color: '#F97316' },
      { text: t('Under Wraps'), color: '#7C3AED' },
      { text: t('Followed'), color: '#10B981' },
    ];
  }, []);

  const currentList = useMemo(() => {
    if (activeTab === 'follows') {
      return followingData;
    } else {
      return followerData;
    }
  }, [followingData, followerData, activeTab]);

  const [userSub, setUserSub] = useState<RecentSubStatusModel>(null);
  const validSubscription = useMemo(() => isNotExpired(userSub?.expired_time), [userSub?.expired_time]);
  // const hasLoginAndSubNormal = useMemo(() => hasNoData, [hasNoData]);

  const isPremium = !!(isLogin && validSubscription);

  useEffect(() => {
    if (isLogin) {
      fetchInsRecentUserSub().then((res) => {
        setUserSub(res.data);
        if (isNotExpired(res.data?.expired_time)) {
          setShowSubscriptionLimit(false);
        }
      });
    } else {
      setUserSub(null);
    }
  }, [isLogin]);

  useEffect(() => {
    if (isLogin && userSub) {
      // 最近订阅
      fetchInsRecentUserSub().then((res) => {
        setUserSub(res.data);
      });
    }
  }, []);

  // 是否当天大于第二次点击
  const isSecondClick = (): boolean => {
    const today = new Date().toISOString().split('T')[0];
    const clickRecord = localStorage.getItem('daily_click_count');
    const record = clickRecord ? JSON.parse(clickRecord) : { date: '', count: 0 };

    if (record.date !== today) {
      // 新的一天，重置计数
      record.date = today;
      record.count = 1;
    } else {
      // 同一天，增加计数
      record.count += 1;
    }

    localStorage.setItem('daily_click_count', JSON.stringify(record));
    return record.count >= 2;
  };

  const handleReveal = async ({
    waitToken = true,
    needTurnstile,
    turnstileToken,
    isFollowing,
    pageNo,
    username,
    isBtnClick,
    data,
  }: {
    waitToken?: boolean;
    needTurnstile?: boolean;
    turnstileToken?: string | null;
    isFollowing: boolean;
    pageNo: number;
    username: string;
    isBtnClick?: boolean;
    data?: any;
  }) => {
    if (!username) return;

    // 第二次
    if (isBtnClick && isSecondClick()) {
      // 无有效订阅时，显示订阅引导弹窗
      if (!validSubscription) {
        setShowSubscriptionLimit(true);
        return;
      }
    }

    setStage(1);
    await new Promise((resolve) => setTimeout(resolve, 10));

    setStage(2);
    setIsLoading(true);

    if (!isLogin && waitToken) {
      nextCallRef.current = (needTurnstile: boolean, turnstileToken: string | null) =>
        handleReveal({
          waitToken: false,
          needTurnstile,
          turnstileToken,
          isFollowing,
          pageNo,
          username,
          isBtnClick,
          data,
        });
      return;
    }
    if (!turnstileToken && needTurnstile) {
      setIsLoading(false);
      setStage(0);
      return message.warning(t('Please verify before continuing.'));
    }
    const dataRes = await fetchRecentSearchData(isFollowing, pageNo, username, needTurnstile && turnstileToken, {
      isPremium,
      bizFuncName: 'recent_follow',
    });
    setIsFirstSearch(false);
    setIsLoading(false);
    setStage(0);
    setShowUserName(username);

    if (pageNo === 1) {
      if (dataRes?.code === 0) {
        const dom = document.getElementById('search-result-user-info');
        document.scrollingElement.scrollTo({
          top: dom.offsetTop,
          behavior: 'smooth',
        });
        if (isFollowing) {
          setFollowingData(dataRes?.data);
        } else {
          setFollowerData(dataRes?.data);
        }
        // 新逻辑
        const status = dataRes?.data?.current_status;
        if (status === 1) {
          setShowSubscriptionLimit(true);
        } else if (status === 2) {
          setOpenRefueingPkg(true);
        }
        return;
      }
    } else {
      if (dataRes?.code === 0) {
        if (isFollowing) {
          setFollowingData({
            items: [...followingData?.items, ...dataRes?.data?.items],
            has_next: dataRes?.data?.has_next,
            code: dataRes?.code,
          });
        } else {
          setFollowerData({
            items: [...followerData?.items, ...dataRes?.data?.items],
            has_next: dataRes?.data?.has_next,
            code: dataRes?.code,
          });
        }
      }
    }

    let code = dataRes?.code;

    if (code !== 0) {
      if (code === 40001) {
        // onOpenLoginModal(true);
        onChangedLoginView(false);
        return;
      } else if (code === 23001) {
        onPayClick();
        return;
      } else if (code === 23002) {
        if (data?.is_private == 1) {
          setOpenPrivateAccount({
            title: t('OopsPrivateProfileDetected'),
            desc: (
              <>
                <div style={{ marginBottom: '24px', textAlign: 'center' }}>
                  {t('ThisInstagramAccountIsPrivate_Desc')}
                </div>
                <UserDashboard {...data} />
              </>
            ),
            btnTitle: t('TRYAGAIN'),
            btnDesc: (
              <p style={{ color: 'var(--app-text-30-color)', marginTop: '10px' }}>
                {t('Note: Only public accounts can be tracked.')}
              </p>
            ),
          });
        } else {
          message.error(dataRes?.message);
          setHasNoData(true);
        }
        return;
      } else if (code === 23999) {
        setOpenRefueingPkg(true);
        // 版本迭代，之前是提示现在加油包
        // setOpenPrivateAccount({
        //   title: t(`Looks like you're maxing out the usual limit.`),
        //   desc: t('Need higher limits for research or monitoring?'),
        //   closeBtnTitle: t('Cancel'),
        //   btnTitle: t('Contact Us'),
        //   action: (isOk: boolean) => {
        //     if (isOk) {
        //       window.open('mailto:info.dolphinradar@gmail.com', '_blank', 'noreferrer');
        //     }
        //   },
        // });
      } else if ([80001, 80002, 80003].includes(code)) {
        message.error(t('Verification failed. Please try again.'));
      } else {
        setOpenPrivateAccount({
          title: t('Invalid Instagram Username'),
          desc: t('The username you entered is not valid. Please check the username and try again.'),
          btnTitle: t('TRYAGAIN'),
        });
      }
    } else {
      setHasNoData(false);
    }
  };

  const handleSearch = async ({
    value,
    waitToken = true,
    needTurnstile,
    turnstileToken,
  }: {
    waitToken?: boolean;
    value: string;
    needTurnstile: boolean;
    turnstileToken: string | null;
  }) => {
    if (isLoading) return;
    setFollowerPageNo(1);
    setFollowingPageNo(1);
    setFollowerData(null);
    setFollowingData(null);
    setTurnstileToken('');
    if (!isLogin && waitToken) {
      nextCallRef.current = (needTurnstile: boolean, turnstileToken: string | null) =>
        handleSearch({
          value,
          waitToken: false,
          needTurnstile,
          turnstileToken,
        });
      return;
    }
    setCurrentInsUser(null);
    setHasNoData(false);
    if (inputSectionRef.current) {
      requestAnimationFrame(() => {
        document.scrollingElement.scrollTo({
          top: inputSectionRef.current.offsetTop - 120,
          behavior: 'smooth',
        });
      });
    }
    if (!turnstileToken && needTurnstile) {
      nextCallRef.current = (needTurnstile: boolean, turnstileToken: string | null) =>
        handleSearch({
          value,
          waitToken: false,
          needTurnstile,
          turnstileToken,
        });
      return message.warning(t('Please verify before continuing.'));
    }
    fetchInsMonitorSearch(value, undefined, { isPremium, bizFuncName: 'recent_follow' })
      .then(async (res) => {
        if (res.code == 0) {
          setCurrentInsUser(res.data[0]);
          handleReveal({
            waitToken: false,
            needTurnstile,
            turnstileToken,
            isFollowing: activeTab === 'follows',
            pageNo: 1,
            username: value,
            isBtnClick: false,
            data: res.data[0],
          });
        } else {
          message.error(res.message);
        }
      })
      .catch(() => {});
  };

  const onPayClick = () => {
    if (!isLogin) {
      onOpenLoginModal(true);
      onChangedLoginView(false);
      return;
    }
    setShowSubscriptionLimit(true);
  };

  const onNext = () => {
    if (stage !== 0) {
      return;
    }
    // if (currentList?.has_next) {
    if (activeTab === 'follows') {
      if (followingData?.items?.length > 0) {
        handleReveal({
          waitToken: false,
          needTurnstile,
          turnstileToken,
          isFollowing: true,
          pageNo: followingPageNo + 1,
          username,
          isBtnClick: true,
        });
        setFollowingPageNo(followingPageNo + 1);
      } else {
        handleReveal({
          waitToken: false,
          needTurnstile,
          turnstileToken,
          isFollowing: true,
          pageNo: 1,
          username,
          isBtnClick: true,
        });
        setFollowingPageNo(1);
      }
    } else {
      if (followerData?.items?.length > 0) {
        handleReveal({
          waitToken: false,
          needTurnstile,
          turnstileToken,
          isFollowing: false,
          pageNo: followerPageNo + 1,
          username,
          isBtnClick: true,
        });
        setFollowerPageNo(followerPageNo + 1);
      } else {
        handleReveal({
          waitToken: false,
          needTurnstile,
          turnstileToken,
          isFollowing: false,
          pageNo: 1,
          username,
          isBtnClick: true,
        });
        setFollowerPageNo(1);
      }
    }
    // } else {
    //   if (currentList?.code === 23001) {
    //     onPayClick()
    //   }
    // }
  };

  function onTurnstileSuccess(token: string) {
    setTurnstileToken(token);
    nextCallRef.current?.(needTurnstile, token);
    nextCallRef.current = null;
  }

  function tabChange() {
    setTurnstileToken('');
    turnstileRef.current?.reset();
  }

  const getSocialImages = useMemo(() => {
    return (
      <div className={styles.socialProof}>
        <div className={styles.socialProofContent}>
          <div className={styles.avatars}>
            {getRandomImages().map((item, i) => (
              <div key={item + i} className={styles.avatarWrapper}>
                <img src={imgUrl(item)} alt="" className={styles.avatarIcon} suppressHydrationWarning />
              </div>
            ))}
          </div>
          <p className={styles.text}>18,600+ {t('users trust DolphinRadar')}</p>
        </div>
        <div className={styles.thumbsUp}>
          {[...Array(3)].map((_, i) => (
            <span key={i} role="img" aria-label="thumbs up">
              👍
            </span>
          ))}
        </div>
      </div>
    );
  }, [getRandomImages]);
  return (
    <div className={styles.recentlyFollowedTool} style={{ minHeight: '100vh' }}>
      <h1 style={{ display: 'none', width: 0, height: 0 }}>{t(`See Anyone's IG Recently Followed Recently`)}</h1>

      <main>
        <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.title}>
              {t("See Anyone's IG Recently Followed")}
              <div className={`${styles.subtitleContainer} ${isLangLanguage && styles.langLanguage}`}>
                <span className={styles['gradient-text']}>{t('Recently')}</span>
                <div className={styles['word-cycle-warp']}>
                  <div className={styles['word-cycle']}>
                    {words.map((word, index) => (
                      <div key={index} className={styles['word-text']} style={{ color: word.color }}>
                        {word.text}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.features}>
            <div className={styles.feature}>
              <Sparkles className={styles.icon} />
              <p>{t('Absolutely Free')}</p>
            </div>
            <div className={styles.feature}>
              <Eye className={styles.icon} />
              <p>{t('100% Anonymous')}</p>
            </div>
            <div className={styles.feature}>
              <Users className={styles.icon} />
              <p>{t('Instant Results')}</p>
            </div>
            <div className={styles.feature}>
              <Clock className={styles.icon} />
              <p>{t('Latest Data')}</p>
            </div>
          </div>

          <div className={styles['input-section']} ref={inputSectionRef}>
            <div className={styles['input-wrapper']}>
              <input
                type="text"
                placeholder={t('Instagram Username (@name)')}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (!username.trim()) {
                      return;
                    }
                    setIsLoading(true);
                    const value = (e.target as HTMLInputElement).value;
                    handleSearch({ value, waitToken: !isFirstSearch, needTurnstile, turnstileToken });
                  }
                }}
                style={{ fontSize: '16px', background: '#FFF', color: '#000' }}
              />
            </div>

            <button
              className={styles['reveal-button']}
              onClick={() => {
                if (!username.trim()) {
                  return;
                }
                setIsLoading(true);
                handleSearch({ value: username, waitToken: !isFirstSearch, needTurnstile, turnstileToken });
              }}
              disabled={isLoading || stage !== 0}
            >
              {isLoading
                ? t('Processing...')
                : stage === 0
                  ? t('Reveal Now')
                  : stage === 1
                    ? t('Capturing')
                    : stage === 2
                      ? t('Analyzing')
                      : t('Starting Run')}
            </button>

            <div className={`${styles.info}`} id="search-result-user-info">
              <Gift className={styles.icon} />
              <span>
                <span className={styles.highlight}>{t('Absolutely FREE!')}</span>{' '}
                <span className={styles.bold}>{t('Even No Login Required — Account Must Be Public')}</span>
              </span>
            </div>

            {getSocialImages}
          </div>

          {/* 当前被搜索的 Instagram 用户的基础信息 */}
          {currentInsUser && currentInsUser?.is_private == 0 && <InstagramUserInfo currentInsUser={currentInsUser} />}
          {needTurnstile && (
            <div className={cn('flex-center', { 'w-0 h-0 overflow-hidden': turnstileToken })}>
              <Turnstile
                ref={turnstileRef}
                key={activeTab}
                as="aside"
                siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY!}
                options={{ theme: 'light', language: router.locale }}
                scriptOptions={{ appendTo: 'body' }}
                onSuccess={onTurnstileSuccess}
                onError={() => setTurnstileToken('')}
                onExpire={() => setTurnstileToken('')}
              />
            </div>
          )}
          <div
            className={cn(styles['results-section'], 'w-[33.875rem] mt-8 mx-auto max-md:w-full max-md:mt-4')}
            style={{ display: isLoading || currentList?.items?.length > 0 || hasNoData ? 'block' : 'none' }}
          >
            {isLoading && (activeTab === 'follows' ? followingPageNo === 1 : followerPageNo === 1) ? (
              <div className={styles.loading}>
                <div className={styles.spinner} />
                <p>{t('Analyzing profile data...')}</p>
              </div>
            ) : (
              <div className={styles.results}>
                {/* 选项卡 */}
                <div className={styles.tabs}>
                  <div
                    className={classNames([styles.tab, activeTab === 'follows' && styles.active])}
                    onClick={() => {
                      setActiveTab('follows');
                      if (!(followingData?.items?.length > 0)) {
                        setFollowingPageNo(1);
                        setTurnstileToken('');
                        handleReveal({
                          waitToken: true,
                          needTurnstile,
                          turnstileToken,
                          isFollowing: true,
                          pageNo: 1,
                          username,
                        });
                      }
                    }}
                  >
                    {t('Recent Follows')}
                  </div>
                  <div
                    className={classNames([styles.tab, activeTab === 'followers' && styles.active])}
                    onClick={async () => {
                      setActiveTab('followers');
                      if (!(followerData?.items?.length > 0)) {
                        setFollowerPageNo(1);
                        setTurnstileToken('');
                        handleReveal({
                          waitToken: true,
                          needTurnstile,
                          turnstileToken,
                          isFollowing: false,
                          pageNo: 1,
                          username,
                        });
                      }
                    }}
                  >
                    {t('Recent Followers')}
                  </div>
                </div>
                {/* 搜索结果 */}
                <div className={cn(styles.content, 'p-8 max-md:p-4')}>
                  <div className="flex items-center gap-4 mb-4">
                    <h2 style={{ marginBottom: 0 }}>
                      @{showUserName} {t('recently')} {activeTab === 'follows' ? 'followed' : 'followed by'}...
                    </h2>
                    <Tooltip
                      title={t('You can click on any user to explore further on Instagram.')}
                      placement="bottomLeft"
                    >
                      <QuestionCircleFilled className="text-[#8E8E8E] cursor-pointer" />
                    </Tooltip>
                  </div>
                  {hasNoData && (
                    <div className="text-center text-black text-[18px] h-[120px] flex items-center justify-center">
                      {activeTab == 'follows'
                        ? t('This account hasn’t followed anyone recently.')
                        : t('This account hasn’t gained any new followers recently.')}
                    </div>
                  )}

                  <div className={styles['user-list']} style={{ marginBottom: '20px' }}>
                    {currentList?.items?.map((user, index) => (
                      <div
                        key={activeTab + index}
                        className={styles.userItem}
                        onClick={() => {
                          window.open(`https://www.instagram.com/${user?.media_name}`, '_blank', 'noreferrer');
                        }}
                      >
                        <span className={classNames([styles.rank, index < 3 && styles['top-3'], 'flex-shrink-0'])}>
                          {index + 1}
                        </span>
                        <div className={cn(styles.avatar, 'flex-shrink-0')}>
                          <LocalImage src={user.media_avatar} alt={user.media_name} width={40} height={40} />
                        </div>
                        <div className={styles['user-info']}>
                          <div className="flex items-center gap-1.5">
                            <p className={styles.username}>@{user.media_name}</p>
                            {user.is_verified && (
                              <Image src={'/images/follow/blue-mark.webp'} alt={'blue-mark'} width={16} height={16} />
                            )}
                            {user.is_private && (
                              <div className="text-sm leading-[1] px-2 py-1 ml-3 rounded-md text-[#F04444] border border-[#FFD1D1] bg-[#FFE9E9]">
                                Private
                              </div>
                            )}
                          </div>
                          <p className={styles['display-name']}>{user.full_name}</p>
                          <p className={styles.stats}></p>
                        </div>
                        <ArrowRight className={styles.arrow} />
                      </div>
                    ))}
                  </div>
                  {currentList?.gender_info && (
                    <div
                      className="mt-6 mb-8 flex justify-center items-center space-x-2 cursor-pointer max-md:mt-[1.25rem] max-md:mb-7"
                      onClick={onNext}
                    >
                      <span className="w-2 h-2 bg-[#C7C7C7] rounded-full"></span>
                      <span className="w-2 h-2 bg-[#C7C7C7] rounded-full"></span>
                      <span className="w-2 h-2 bg-[#C7C7C7] rounded-full"></span>
                    </div>
                  )}
                  {/* 性别分布 */}
                  {currentList?.gender_info && (
                    <GenderDistribution
                      male={currentList?.gender_info?.boys}
                      female={currentList?.gender_info?.girls}
                      other={currentList?.gender_info?.others}
                      onClick={onNext}
                    />
                  )}
                  <div className={styles['load-more']}>
                    <p className="mt-6 text-[1rem] leading-5 max-md:mt-[1.125rem] max-md:text-[calc(0.875rem-1px)] max-md:leading-4">
                      {t('Get complete insights with DolphinRadar Analytics')}
                    </p>
                    <Button
                      className="w-[19.5rem] h-[48px] mt-8 mb-4 rounded-md max-md:w-full max-md:mt-4 max-md:mb-2"
                      loading={isLoading}
                      onClick={onNext}
                    >
                      {currentList?.has_next ? t('Load More Truth') : t('Unlock Full Analytics')}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <UserUpdates />
        <div className={styles.container}>
          <FAQSection />
          <HowItWorksSection />
        </div>
        <ContrastCheck />
        <RealStore />
        <ProductionFunction />
        <div className={styles.container}>
          <UserReviewsSection />
          <ExploreMoreToolsSection />
        </div>
      </main>
      <UpgradeModal open={showSubscriptionLimit} closeAction={() => setShowSubscriptionLimit(false)} />
      {/* 输入错误弹窗 */}
      <CommonSimpleModal
        open={openPrivateAccount !== null}
        title={openPrivateAccount?.title}
        desc={openPrivateAccount?.desc}
        btnTitle={openPrivateAccount?.btnTitle}
        closeBtnTitle={openPrivateAccount?.closeBtnTitle}
        btnDesc={openPrivateAccount?.btnDesc}
        closeAction={(...arg) => {
          const res = openPrivateAccount?.action?.(...arg);
          if (res instanceof Promise) {
            res.then(() => {
              setOpenPrivateAccount(null);
            });
          } else {
            setOpenPrivateAccount(null);
          }
        }}
      />
      {/* 加油包 */}
      <RefueingPkg open={openRefueingPkg} onClose={() => setOpenRefueingPkg(false)} />
    </div>
  );
}
